/**
 * learning/dialogContent.tsx — the Learning dialogs' CONTENT and the one rule that derives from it:
 * the FAQ's questions and answers, the self-assessment's questions, and deriveLevel.
 *
 * No design components, no dialog chrome, no fetch — so both the legacy Tailwind dialogs and the Kombi
 * ones can render exactly this copy. Extracted rather than copied for the reason the rest of this
 * folder was: a second copy of an eleven-answer FAQ and a five-question assessment drifts invisibly,
 * because each side still reads correctly on its own. `deriveLevel` in particular must not fork — it
 * decides the level that gets WRITTEN to a person's profile.
 *
 * It is .tsx rather than .ts because one FAQ answer carries links.
 */
import type { ReactNode } from 'react';
import type { CompetencyLevel, MakerType } from './api';

/** The levels and maker types the assessment offers, in order. Pure data, shared by both dialog sets. */
export const LEVELS: readonly CompetencyLevel[] = ['Beginner', 'Practitioner', 'Advanced'];
export const MAKER_TYPES: readonly MakerType[] = ['Business user', 'Maker', 'Pro-developer'];

// ── About / FAQ dialog ───────────────────────────────────────────────────────────────────────
//
// The block that explains how the platform works, for the new joiner who skipped the first-run tour
// and for anyone who has forgotten what `adopt` means. It is EXPLANATORY ONLY: it changes no access
// and collects nothing (PC-F01/PC-F04), which is why it is plain content and not a form.
//
// TWO RULES SHAPE THE COPY. It uses the canonical vocabulary and only it (PC §6.2) — adopt, save,
// like, review, Approved — because a paraphrase here is how "install" and "download" come back. And it
// describes ONLY what is live today (PC-F14): the §5.4 captioned video demos are a separate, later
// piece of work (LH-F24), so nothing below promises a video, and no answer describes a capability the
// platform does not yet have.

/** One question-and-answer pair. A closed set, defined here beside the copy it labels. */
export interface AboutFaq {
  q: string;
  /** ReactNode, not string, so the "where do I get help?" answer can carry its two links. */
  a: ReactNode;
}

export const ABOUT_FAQS: readonly AboutFaq[] = [
  {
    q: 'What is Trailblazer for?',
    a: 'Colleagues across the business build small tools — automations, apps and AI assistants — that save real time. Trailblazer is where those tools are shared, so the next person who has the same problem can find and reuse one instead of rebuilding it.',
  },
  {
    q: 'Is there anything I have to do first?',
    a: 'The AI Awareness training is mandatory, and it is the same rules and the same knowledge check for everyone. Nothing on the platform is locked behind it: it is a compliance requirement, not a key. Your self-assessed level only changes what is suggested to you first, never what you can see.',
  },
  {
    q: 'What does adopt mean — and how is it different from save and like?',
    a: 'Adopt gives you your own copy of a tool to use and adapt, and it is the action that counts as reuse. Save keeps a tool on your own private list, visible to nobody else. Like is public appreciation for the person who built it. All three are yours to use as you like; only adopt makes a copy.',
  },
  {
    q: 'Why does every tool need reviewing?',
    a: 'A tool is reviewed before it is published, and only an Approved tool appears in the catalogue. Review protects the builder as much as the adopter — it is the reason a colleague can trust what they adopt. If a tool is not approved, the builder gets the reason and can fix it and submit again.',
  },
  {
    q: 'Does my manager see what I do here?',
    a: 'No. Trailblazer ranks tools, never people, and nothing here reports your activity to your line manager. What you save is private to you. Management sees aggregate figures only, and any figure covering too few people to report is withheld rather than shown — that is why a number sometimes reads "too few to report" instead of appearing.',
  },
  {
    // This answer carries what the deleted "Learn with people" section said (UR-F10, the department
    // support-champion fallback), so its old "see 'Learn with people' above" pointer became the
    // substance. The About section states the same route in one line on the page surface, for anyone
    // who follows the "Learn in person" tile and never opens a dialog.
    q: 'Where do I get help?',
    a: (
      <>
        Your department support champion first — you can find yours from your{' '}
        <a className="lh-prose-link" href="/me">
          profile
        </a>
        , or ask in the{' '}
        <a className="lh-prose-link" href="/community">
          Community
        </a>
        , where colleagues share what worked. A tool’s own page is where you report a problem with
        it.
      </>
    ),
  },
];

// ── self-assessment dialog ───────────────────────────────────────────────────────────────────

export interface AssessmentQuestion {
  id: string;
  prompt: string;
  /** Each option maps to the level it suggests. Borderline answers bias DOWN (§3). */
  options: { label: string; level: CompetencyLevel }[];
}

export const ASSESSMENT_QUESTIONS: readonly AssessmentQuestion[] = [
  {
    id: 'q1',
    prompt: 'How would you describe your experience building with low-code or AI tools?',
    options: [
      { label: 'I have not built anything yet', level: 'Beginner' },
      { label: 'I have built a few simple tools', level: 'Practitioner' },
      { label: 'I build and maintain tools others rely on', level: 'Advanced' },
    ],
  },
  {
    id: 'q2',
    prompt: 'How comfortable are you with expressions, conditions, and connecting data sources?',
    options: [
      { label: 'New to these', level: 'Beginner' },
      { label: 'I can use them for everyday tasks', level: 'Practitioner' },
      { label: 'I design data models and complex logic', level: 'Advanced' },
    ],
  },
  {
    id: 'q3',
    prompt: 'When you think about governance and reuse, which fits best?',
    options: [
      { label: 'I am still learning the rules', level: 'Beginner' },
      { label: 'I follow the rules and share the odd tool', level: 'Practitioner' },
      { label: 'I design for reuse and lifecycle across teams', level: 'Advanced' },
    ],
  },
  {
    id: 'q4',
    prompt: 'Which kind of task do you most often want help with?',
    options: [
      { label: 'Getting started at all', level: 'Beginner' },
      { label: 'Making an existing idea more robust', level: 'Practitioner' },
      { label: 'Scaling or governing what I have built', level: 'Advanced' },
    ],
  },
];

export const LEVEL_RANK: Record<CompetencyLevel, number> = {
  Beginner: 0,
  Practitioner: 1,
  Advanced: 2,
};

/**
 * Derive a suggested level from the answers. §3: borderline answers bias DOWN — so we take the
 * MEDIAN-floor of the chosen levels rather than the max, which keeps a mixed profile at the more
 * conservative level. (A single Advanced answer among Beginner answers does not make someone
 * Advanced.)
 */

export function deriveLevel(chosen: CompetencyLevel[]): CompetencyLevel {
  if (chosen.length === 0) return 'Beginner';
  const ranks = chosen.map((l) => LEVEL_RANK[l]).sort((a, b) => a - b);
  // Floor of the median → biases down on an even split (e.g. [B,B,P,A] → index 1 = Beginner).
  const midIndex = Math.floor((ranks.length - 1) / 2);
  const rank = ranks[midIndex] ?? 0;
  return LEVELS[rank] ?? 'Beginner';
}
